/**
 * In-app PDF passage viewer for lease-return sources.
 * Renders the page with colored highlights (return = green, next = blue, else yellow)
 * and shows the extracted text with the same highlight. Falls back to iframe + text.
 * Highlights only the contiguous region that matches the excerpt, and supports
 * browsing neighbouring pages when a passage spans more than one page.
 */

let pdfjsLibPromise = null;

const COLORS = {
  return: { fill: "rgba(34, 197, 94, 0.38)", mark: "#86efac", label: "Return" },
  next: { fill: "rgba(59, 130, 246, 0.38)", mark: "#93c5fd", label: "Next" },
  yellow: { fill: "rgba(250, 204, 21, 0.45)", mark: "#fde047", label: "Passage" },
};

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function highlightHtml(text, query, markColor) {
  const esc = escapeHtml(text);
  const q = String(query || "").trim();
  if (!q || q.length < 4) return esc;
  const snippet = q.length > 160 ? q.slice(0, 160) : q;
  const safe = snippet.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");
  try {
    return esc.replace(
      new RegExp(`(${safe})`, "gi"),
      `<mark style="background:${markColor};padding:0 .15rem;border-radius:.15rem">$1</mark>`
    );
  } catch {
    return esc;
  }
}

async function loadPdfJs() {
  if (pdfjsLibPromise) return pdfjsLibPromise;
  const src = "/v2/pdf.min.mjs";
  pdfjsLibPromise = import(src)
    .then((mod) => {
      const pdfjs = mod.default || mod;
      if (pdfjs?.GlobalWorkerOptions) {
        pdfjs.GlobalWorkerOptions.workerSrc = "/v2/pdf.worker.min.mjs";
      }
      return pdfjs;
    })
    .catch((err) => {
      console.warn("[lease-return] pdf.js unavailable", err?.message || err);
      return null;
    });
  return pdfjsLibPromise;
}

function normalize(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Locate the excerpt as ONE contiguous region of the page text and return
 * rects only for the text items inside that region. Falls back to a tight
 * rare-word match (max 12 items) when no contiguous run is found.
 */
function highlightRectsFromItems(items, viewport, excerpt, pdfjs) {
  const needle = normalize(excerpt);
  if (needle.length < 8 || !items?.length) return { rects: [], matched: false };

  // Concatenated normalized page text with per-item [start,end) offsets.
  const spans = [];
  let pageStr = "";
  for (let i = 0; i < items.length; i += 1) {
    const str = normalize(items[i].str);
    const start = pageStr.length;
    pageStr += (start ? " " : "") + str;
    spans.push({ idx: i, start: start ? start + 1 : 0, end: pageStr.length, empty: !str });
  }

  // Try the full excerpt, then progressively shorter word-prefixes.
  const words = needle.split(" ").filter(Boolean);
  let matchStart = -1;
  let matchEnd = -1;
  const attempts = [];
  attempts.push(words);
  for (const n of [16, 12, 8, 6, 5, 4]) {
    if (words.length > n) attempts.push(words.slice(0, n));
  }
  for (const attempt of attempts) {
    const probe = attempt.join(" ");
    if (probe.length < 10) continue;
    const at = pageStr.indexOf(probe);
    if (at >= 0) {
      matchStart = at;
      matchEnd = at + (attempt === words ? probe.length : normalizeLenGuess(words, pageStr, at));
      break;
    }
  }

  const toRect = (item) => {
    try {
      const tx = pdfjs.Util.transform(viewport.transform, item.transform);
      const h = item.height * viewport.scale;
      return { x: tx[4], y: tx[5] - h, w: item.width * viewport.scale, h };
    } catch {
      return null;
    }
  };

  if (matchStart >= 0) {
    const rects = [];
    for (const sp of spans) {
      if (sp.empty) continue;
      if (sp.end <= matchStart || sp.start >= matchEnd) continue;
      const r = toRect(items[sp.idx]);
      if (r) rects.push(r);
    }
    return { rects: rects.slice(0, 40), matched: rects.length > 0 };
  }

  // Fallback: items containing rare (long) words of the excerpt — tightly capped.
  const rare = words.filter((w) => w.length >= 6).slice(0, 8);
  if (!rare.length) return { rects: [], matched: false };
  const rects = [];
  for (const sp of spans) {
    if (rects.length >= 12) break;
    if (sp.empty) continue;
    const str = normalize(items[sp.idx].str);
    if (rare.some((w) => str.includes(w))) {
      const r = toRect(items[sp.idx]);
      if (r) rects.push(r);
    }
  }
  return { rects, matched: false };
}

/** When only a prefix matched, extend the region to roughly the excerpt length. */
function normalizeLenGuess(words, pageStr, at) {
  const full = words.join(" ");
  return Math.min(full.length, pageStr.length - at);
}

export function openPassageViewer(
  el,
  { documentId, page, excerpt, color = "yellow", title, apiUrl, fetchJson }
) {
  const tone = COLORS[color] || COLORS.yellow;
  let pageN = Number(page) || 1;
  let numPages = null;
  let pdfDoc = null;
  document.querySelectorAll("[data-lr-pdf]").forEach((n) => n.remove());

  const canvas = el("canvas", { class: "lr-pdf-canvas" });
  const overlay = el("canvas", { class: "lr-pdf-overlay" });
  const stage = el("div", { class: "lr-pdf-stage" }, [canvas, overlay]);
  const textHost = el("div", { class: "lr-pdf-text" });
  const statusEl = el("div", { class: "lr-pdf-status", text: "Loading document…" });
  const pageLabel = el("span", { class: "text-xs text-gray-500", text: `p.${pageN}` });
  const prevBtn = el("button", { type: "button", class: "lr-icon", title: "Previous page", text: "‹" });
  const nextBtn = el("button", { type: "button", class: "lr-icon", title: "Next page", text: "›" });

  const backdrop = el("div", { class: "lr-pdf-backdrop", "data-lr-pdf": "1" }, [
    el("div", { class: "lr-pdf-modal", role: "dialog", "aria-modal": "true" }, [
      el("div", { class: "lr-pdf-head" }, [
        el("div", { class: "min-w-0" }, [
          el("div", { class: "lr-pdf-title", text: title || `Document #${documentId}` }),
          el("div", { class: "flex flex-wrap items-center gap-2 mt-0.5" }, [
            prevBtn,
            pageLabel,
            nextBtn,
            el("span", {
              class: "lr-pdf-chip",
              style: `background:${tone.mark}`,
              text: tone.label,
            }),
          ]),
        ]),
        el("button", {
          type: "button",
          class: "lr-icon",
          title: "Close",
          text: "×",
          onclick: () => backdrop.remove(),
        }),
      ]),
      statusEl,
      el("div", { class: "lr-pdf-grid" }, [
        el("div", { class: "lr-pdf-pane" }, [
          el("div", { class: "lr-pdf-pane-h", text: "Original" }),
          stage,
        ]),
        el("div", { class: "lr-pdf-pane" }, [
          el("div", { class: "lr-pdf-pane-h", text: "Extracted text" }),
          textHost,
        ]),
      ]),
    ]),
  ]);
  backdrop.addEventListener("click", (ev) => {
    if (ev.target === backdrop) backdrop.remove();
  });
  document.body.appendChild(backdrop);

  const syncNav = () => {
    pageLabel.textContent = numPages ? `p.${pageN} / ${numPages}` : `p.${pageN}`;
    prevBtn.disabled = pageN <= 1;
    nextBtn.disabled = numPages ? pageN >= numPages : false;
    prevBtn.style.opacity = prevBtn.disabled ? ".35" : "1";
    nextBtn.style.opacity = nextBtn.disabled ? ".35" : "1";
  };

  const loadText = async () => {
    try {
      const pg = await fetchJson(apiUrl(`/api/ai/documents/${documentId}/pages/${pageN}`)).catch(
        () => null
      );
      const pageText = (pg?.pages || []).map((p) => p.text || "").join("\n\n");
      textHost.innerHTML = highlightHtml(
        pageText || "(no extracted text for this page)",
        excerpt,
        tone.mark
      );
      const mark = textHost.querySelector("mark");
      if (mark) mark.scrollIntoView({ block: "center" });
    } catch (e) {
      textHost.textContent = e?.message || "Could not load extracted text.";
    }
  };

  const renderPdfPage = async () => {
    if (!pdfDoc) return false;
    const pdfjs = await loadPdfJs();
    const pdfPage = await pdfDoc.getPage(Math.min(Math.max(pageN, 1), pdfDoc.numPages));
    const viewport = pdfPage.getViewport({ scale: 1.35 });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    overlay.width = viewport.width;
    overlay.height = viewport.height;
    canvas.style.width = "100%";
    overlay.style.width = "100%";
    await pdfPage.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    const tc = await pdfPage.getTextContent();
    const { rects, matched } = highlightRectsFromItems(tc.items || [], viewport, excerpt, pdfjs);
    const ctx = overlay.getContext("2d");
    ctx.clearRect(0, 0, overlay.width, overlay.height);
    ctx.fillStyle = tone.fill;
    for (const r of rects) ctx.fillRect(r.x, r.y, r.w, Math.max(r.h, 8));
    statusEl.textContent = matched
      ? `Passage highlighted on p.${pageN}.`
      : rects.length
        ? `Best-effort match on p.${pageN} — check the extracted text on the right.`
        : `Opened p.${pageN} — passage not on this page; use ‹ › to check neighbouring pages.`;
    return matched;
  };

  const showPage = async () => {
    syncNav();
    statusEl.textContent = `Loading p.${pageN}…`;
    await loadText();
    try {
      await renderPdfPage();
    } catch (err) {
      console.warn("[lease-return] pdf render", err?.message || err);
      const fileUrl = apiUrl(`/api/ai/documents/${documentId}/file`);
      stage.replaceChildren(
        el("iframe", {
          class: "lr-pdf-iframe",
          title: "Original file",
          src: `${fileUrl}#page=${pageN}`,
        })
      );
      statusEl.textContent = `Opened p.${pageN} — highlighted in the extracted text`;
    }
    syncNav();
  };

  prevBtn.onclick = () => {
    if (pageN > 1) {
      pageN -= 1;
      showPage();
    }
  };
  nextBtn.onclick = () => {
    if (!numPages || pageN < numPages) {
      pageN += 1;
      showPage();
    }
  };

  (async () => {
    const fileUrl = apiUrl(`/api/ai/documents/${documentId}/file`);
    const pdfjs = await loadPdfJs();
    try {
      if (!pdfjs?.getDocument) throw new Error("pdf.js missing");
      const res = await fetch(fileUrl, { credentials: "include" });
      if (!res.ok) throw new Error(`PDF HTTP ${res.status}`);
      const data = await res.arrayBuffer();
      pdfDoc = await pdfjs.getDocument({ data }).promise;
      numPages = pdfDoc.numPages;
    } catch (err) {
      console.warn("[lease-return] pdf load", err?.message || err);
      pdfDoc = null;
    }
    await showPage();
    // If the passage isn't on the cited page, quietly look one page either side.
    if (pdfDoc && excerpt && String(excerpt).trim().length >= 12) {
      const here = await pageHasPassage(pageN);
      if (!here) {
        for (const cand of [pageN + 1, pageN - 1]) {
          if (cand < 1 || (numPages && cand > numPages)) continue;
          if (await pageHasPassage(cand)) {
            pageN = cand;
            await showPage();
            break;
          }
        }
      }
    }
  })();

  async function pageHasPassage(n) {
    try {
      const pdfjs = await loadPdfJs();
      const pdfPage = await pdfDoc.getPage(n);
      const tc = await pdfPage.getTextContent();
      const viewport = pdfPage.getViewport({ scale: 1 });
      const { matched } = highlightRectsFromItems(tc.items || [], viewport, excerpt, pdfjs);
      return matched;
    } catch {
      return false;
    }
  }
}
